import { useEffect, memo, useCallback, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { useSaveButton } from 'hooks/useSaveButton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import {
  CONFIG_DDF,
  CONFIG_DDF_RCE_EDIT,
  CONFIG_SECOND_DEBTOR,
  isFFGConfigWithAutoRedirectOnDacSuccess,
  isRceEditConfig
} from 'config/utils/config'
import { NavigateFunction } from 'react-router-dom'
import LegalHintTexts from 'texts/common/copy/legal'
import {
  RegisteredEventName,
  broadcastToParent
} from '@finanzcheck/catalyst-pollard'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import { usePayoutAccountValidation } from 'hooks/usePayoutAccountValidation'
import { getButtonSizes } from './getButtonSizes'
import FormNavigation from './FormNavigationTemplate'
import { getAge } from 'utils/date'
import { AgeGroup } from 'constants/components'
import { useAgeGroup } from 'stores/utils/hooks/useAgeGroup'
import { Button } from '@mui/material'
import { isYoungOrMiddleAgeGroup } from 'stores/helpers/AgeGroups'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { isPremiumCustomer } from 'utils/user'
import { Experiments } from 'CustomOptimizelyProvider'

type Props = {
  onBack?: () => void
  onNext: () => void
  legalContact: string
  navigate: NavigateFunction
}

const FFGFormNavigation = observer(({ onBack, onNext, navigate }: Props) => {
  const {
    rootStore: {
      experimentData,
      setIsSkipDacCTAVisible,
      isDacShowOnCtaClick,
      isInIframe,
      urlParams: { backUrl },
      traversal: {
        data: {
          common: { amount },
          debtors: {
            primary: {
              personal: { birthday },
              income
            }
          }
        }
      },
      page: { config }
    }
  } = useRootStore()

  const {
    page: { isFirstDisplayedPage, isLastDisplayedPage, getCurrentIndex },
    navigation: { currentPageInSession, isDacPage },
    traversal: { data },
    traversalSession: { dacWidgetState }
  } = useStores()

  const ageGroup = useAgeGroup()
  const debtorsAge = getAge(birthday)
  const isYoungAgeConditionMet =
    ageGroup === AgeGroup.YOUNG_AGE_GROUP ||
    ageGroup === AgeGroup.TOO_YOUNG_AGE_GROUP

  const isMiddleAgeGroup = ageGroup === AgeGroup.MIDDLE_AGE_GROUP
  const isInRceRoute = isRceEditConfig(config.name)

  const isPayoutAccountReady = usePayoutAccountValidation(true)

  const { isSaveButtonVisible } = useSaveButton({
    onSuccess: () => {},
    onError: () => {}
  })

  const [showExperimentDacSkipButton, setShowExperimentDacSkipButton] =
    useState(false)

  const [skipButtonText, setSkipButtonText] = useState('')

  const [skipButtonShownReason, setSkipButtonShownReason] = useState('')

  const isUserInExperiment =
    // @ts-ignore - experimentData type needs refinement for dynamic access
    experimentData[Experiments.CAT_4359_DAC_SKIP_BUTTON_V3] ===
      PossibleVariants.VARIATION_B ||
    // @ts-ignore - experimentData type needs refinement for dynamic access
    experimentData[Experiments.CAT_4359_DAC_SKIP_BUTTON_V3] ===
      PossibleVariants.VARIATION_C

  const isPremium =
    isPremiumCustomer({ debtorsAge, amount, income }) ||
    ageGroup === AgeGroup.OLD_AGE_GROUP

  const isUserInExperimentRef = useRef(isUserInExperiment)

  const showExperimentDacSkipButtonRef = useRef(showExperimentDacSkipButton)

  useEffect(() => {
    isUserInExperimentRef.current = isUserInExperiment
  }, [isUserInExperiment])

  useEffect(() => {
    showExperimentDacSkipButtonRef.current = showExperimentDacSkipButton
  }, [showExperimentDacSkipButton])

  const currentIndex = getCurrentIndex()

  const trackWithOrigin = (event: AmplitudeEvent, payload = {}) => {
    trackInAmplitude(event, { origin: 'taurine', ...payload })
  }

  const onNextWithTracking = useCallback(() => {
    if (ageGroup === AgeGroup.MIDDLE_AGE_GROUP && isDacPage) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'skip_dac_button_click_on_dac_page',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }
    onNext()
  }, [onNext, data, isDacPage])

  const onBackHandler = () => {
    const isFinalizingOnBackButton =
      ageGroup === AgeGroup.YOUNG_AGE_GROUP &&
      isDacPage &&
      !isUserInExperiment &&
      config.name !== CONFIG_DDF_RCE_EDIT

    if (isLastPage) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'back_button_click_on_dac_page',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }

    if (isFinalizingOnBackButton) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'final_on_back_button_click',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
      onNext()
      return
    }
    onBack && onBack()
  }

  const isLastPage = isLastDisplayedPage(currentPageInSession)

  useEffect(() => {
    if (
      (isYoungAgeConditionMet ||
        isFFGConfigWithAutoRedirectOnDacSuccess(config.name)) &&
      dacWidgetState === 'success'
    ) {
      trackWithOrigin(AmplitudeEvent.OnDACSuccessAutoRedirect)
      onNextWithTracking()
    }
  }, [dacWidgetState, config.name])

  useEffect(() => {
    if (dacWidgetState === 'error' && isUserInExperiment && !isPremium) {
      setShowExperimentDacSkipButton(true)
      setSkipButtonShownReason('error_in_dac')
      setSkipButtonText('Weiter ohne digitalen Kontoauszug')
    }
  }, [dacWidgetState, isUserInExperiment, isPremium])

  useEffect(() => {
    if (isDacShowOnCtaClick) {
      setShowExperimentDacSkipButton(isDacShowOnCtaClick)
      setSkipButtonShownReason('dac_widget_loaded_with_show_on_cta_click')
    }
  }, [isDacShowOnCtaClick])

  useEffect(() => {
    if (showExperimentDacSkipButton && skipButtonShownReason) {
      setIsSkipDacCTAVisible(true)
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'skip_button_shown',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          },
          payload: {
            shown_reason: skipButtonShownReason,
            text_on_button: skipButtonText
          }
        }
      })
    }
  }, [showExperimentDacSkipButton, skipButtonText, skipButtonShownReason])

  const skipDacButtonOnClick = useCallback(() => {
    if (ageGroup === AgeGroup.MIDDLE_AGE_GROUP && isDacPage) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'skip_dac_button_click_on_dac_page',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
            ...getOptimizelyTrackingAttribute(data)
          }
        }
      })
    }

    broadcastToParent({
      eventName: RegisteredEventName.tracking,
      data: {
        event: 'skip_button_clicked',
        context: {
          Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
          ...getOptimizelyTrackingAttribute(data)
        },
        payload: {
          text_on_button: skipButtonText
        }
      }
    })
    onNext()
  }, [onNext, skipButtonText])

  useEffect(() => {
    if (isUserInExperiment) {
      setSkipButtonText(
        isPremium
          ? 'Weiter ohne digitalen Kontoauszug'
          : 'Ich habe keinen Online-Banking-Zugang'
      )
      return
    }

    setSkipButtonText(
      isInRceRoute || isMiddleAgeGroup
        ? 'Ich habe keinen Online-Banking-Zugang'
        : 'Kontoauszüge manuell hochladen'
    )
  }, [isInRceRoute, isMiddleAgeGroup, isUserInExperiment, isPremium])

  useEffect(() => {
    if (!isDacPage) return
    const timeOut = isUserInExperiment
      ? isPremium
        ? 15000
        : ageGroup === AgeGroup.YOUNG_AGE_GROUP
          ? 60000
          : 0
      : 0
    const timeoutId = window.setTimeout(() => {
      if (showExperimentDacSkipButtonRef.current) {
        return
      }
      if (ageGroup === AgeGroup.MIDDLE_AGE_GROUP && !isPremium) {
        setShowExperimentDacSkipButton(isUserInExperimentRef.current)
        return
      }
      setSkipButtonShownReason('timer')
      setShowExperimentDacSkipButton(isUserInExperimentRef.current)
    }, timeOut)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [isUserInExperiment, isPremium, isDacPage, ageGroup])

  useEffect(() => {
    const onTabVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') {
        return
      }
      if (
        !isUserInExperiment ||
        !isDacPage ||
        !isYoungOrMiddleAgeGroup ||
        isPremium
      ) {
        return
      }
      setSkipButtonShownReason('left_tab')
      setShowExperimentDacSkipButton(true)
      setSkipButtonText('Weiter ohne digitalen Kontoauszug')
    }
    document.addEventListener('visibilitychange', onTabVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onTabVisibilityChange)
    }
  }, [isUserInExperiment, isDacPage, isYoungOrMiddleAgeGroup, isPremium])

  const isFirstPage = isFirstDisplayedPage(currentPageInSession)

  const backLabel =
    !isInIframe || currentIndex > 0 || backUrl ? 'Zurück' : undefined

  const isBackButtonHidden = !backLabel || isFirstPage

  let submitLabel = isLastPage ? 'Angebote ansehen' : 'Weiter'

  const getIsContinueButtonHidden = () => {
    if (config.name === CONFIG_SECOND_DEBTOR) {
      return false
    }

    if (config.name === CONFIG_DDF) {
      if (
        (isDacPage && dacWidgetState !== 'success') ||
        (ageGroup !== AgeGroup.OLD_AGE_GROUP && isDacPage)
      ) {
        return true
      }

      return false
    }

    return (
      isLastPage &&
      isPayoutAccountReady &&
      dacWidgetState !== 'success' &&
      isYoungOrMiddleAgeGroup(debtorsAge)
    )
  }

  const isContinueButtonHidden = getIsContinueButtonHidden()

  const { sizeOfContinueButton, sizeOfBackButton, sizeOfSaveButton } =
    getButtonSizes(
      isBackButtonHidden,
      isSaveButtonVisible,
      isContinueButtonHidden
    )

  const showSkipDacButton =
    (ageGroup === AgeGroup.MIDDLE_AGE_GROUP &&
      isLastPage &&
      isDacPage &&
      !isUserInExperiment) ||
    (isInRceRoute && isDacPage)
  const showDacLegalText =
    isLastPage &&
    isPayoutAccountReady &&
    isDacPage &&
    config.name === CONFIG_DDF

  return (
    <FormNavigation
      onBack={onBackHandler}
      onNext={onNext}
      navigate={navigate}
      legalText={LegalHintTexts.defaultLegalHint}
      isContinueWithoutDacVisible={false}
      isBackButtonHidden={isBackButtonHidden}
      isContinueButtonHidden={isContinueButtonHidden}
      sizeOfContinueButton={showSkipDacButton ? 12 : sizeOfContinueButton}
      sizeOfBackButton={sizeOfBackButton}
      sizeOfSaveButton={
        isDacPage && (showSkipDacButton || showExperimentDacSkipButton)
          ? isUserInExperiment
            ? 4
            : 12
          : sizeOfSaveButton
      }
      submitLabel={submitLabel}
      backLabel={backLabel}
      sizeOfSkipDacButton={isUserInExperiment ? 8 : 12}
      onNextWithTracking={onNextWithTracking}
      trackWithOrigin={trackWithOrigin}
      continueButtonColor={'primary'}
      continueWithoutDacButtonVariant={'text'}
      skipDacButton={
        isDacPage && (showSkipDacButton || showExperimentDacSkipButton) ? (
          <Button
            id="test-skip-dac"
            name="test-skip-dac"
            onClick={skipDacButtonOnClick}
            color="primary"
            fullWidth
            variant={
              config.name === CONFIG_DDF && !isUserInExperiment
                ? 'contained'
                : 'outlined'
            }
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              height: '50px'
            }}
          >
            {skipButtonText}
          </Button>
        ) : null
      }
      showDacLegalText={showDacLegalText}
      backButtonVariant="text"
      saveButtonVariant={'outlined'}
    />
  )
})

export default memo(FFGFormNavigation)
